import { supabase } from '../lib/supabase';
import caterpillarTemplate from '../configs/caterpillar-template.json';
import caterpillarAntaresAsnTemplate from '../configs/caterpillar-antares-template.json';
import caterpillarAntaresInvoiceTemplate from '../configs/caterpillar-antares-invoice-template.json';
import genericTemplate from '../configs/generic-template.json';

/**
 * Create a template and assign it to a trading partner via the junction table.
 * If the template already exists for this partner, skips creation.
 */
async function createTemplateAndAssign(
  partnerId: string,
  partnerName: string,
  templateConfig: Record<string, unknown>,
  documentType: string,
  version: string = '1.0'
) {
  // Check if an assignment already exists for this partner + doc type
  const { data: existingAssignments } = await supabase
    .from('tp_template_assignments')
    .select('template_id')
    .eq('trading_partner_id', partnerId);

  if (existingAssignments && existingAssignments.length > 0) {
    // Check if any assigned template matches the doc type
    for (const assignment of existingAssignments) {
      const { data: tpl } = await supabase
        .from('trading_partner_templates')
        .select('id, document_type')
        .eq('id', assignment.template_id)
        .eq('document_type', documentType)
        .eq('is_current', true)
        .maybeSingle();
      if (tpl) {
        console.log(`${partnerName} already has an active ${documentType} template`);
        return tpl.id;
      }
    }
  }

  // Create the template (standalone — no trading_partner_id column)
  const { data: template, error } = await supabase
    .from('trading_partner_templates')
    .insert({
      version,
      is_current: true,
      template_config: templateConfig,
      document_type: documentType,
    })
    .select()
    .single();

  if (error) {
    console.error(`Error creating ${documentType} template for ${partnerName}:`, error);
    return null;
  }

  // Create the junction row
  const { error: assignError } = await supabase
    .from('tp_template_assignments')
    .insert({
      trading_partner_id: partnerId,
      template_id: template.id,
    });

  if (assignError) {
    console.error(`Error assigning template to ${partnerName}:`, assignError);
    return null;
  }

  console.log(`Created ${documentType} template for ${partnerName}:`, template.id);
  return template.id;
}

async function getOrCreatePartner(
  code: string,
  name: string,
  description: string
): Promise<string | null> {
  const { data: existing } = await supabase
    .from('trading_partners')
    .select('id')
    .eq('code', code)
    .maybeSingle();

  if (existing) {
    console.log(`${name} trading partner already exists`);
    return existing.id;
  }

  const { data: partner, error } = await supabase
    .from('trading_partners')
    .insert({ code, name, description, is_active: true })
    .select()
    .single();

  if (error) {
    console.error(`Error creating ${name}:`, error);
    return null;
  }

  console.log(`Created ${name} trading partner:`, partner.id);
  return partner.id;
}

export async function seedCaterpillarTradingPartner() {
  try {
    const partnerId = await getOrCreatePartner(
      'CAT',
      'Caterpillar Inc.',
      'Global manufacturer of construction and mining equipment. Requires X12 4060 compliant ASN documents with specific mandatory fields and segment ordering.'
    );
    if (!partnerId) return { success: false, error: 'Failed to create partner' };

    const templateId = await createTemplateAndAssign(
      partnerId, 'Caterpillar', caterpillarTemplate as Record<string, unknown>, '856'
    );

    return { success: true, partner: partnerId, template: templateId };
  } catch (error) {
    console.error('Error seeding Caterpillar:', error);
    return { success: false, error };
  }
}

/**
 * Seed the Caterpillar Antares (2V) trading partner — a Caterpillar division
 * addressed exclusively by US Nameplate (account 1621). The ASN and Invoice
 * templates carry the US Nameplate specifics directly (SE/SF defaults,
 * Mt Vernon address, G830 shipping charge prefill) because no other
 * customer routes documents to this TP. If that assumption ever changes,
 * we need per-(customer, TP) template scoping — tracked as a follow-up.
 *
 * Partner code '5130' matches the reports partners.id for Cat Antares.
 */
export async function seedCaterpillarAntaresTradingPartner() {
  try {
    const partnerId = await getOrCreatePartner(
      '5130',
      'Caterpillar Antares — All Business (2V)',
      'Caterpillar Antares division (2V). Ships via UPS small package (prepaid) with carton packaging, plant-code addressing (qualifier 92), and the Mt Vernon IA ship-from defaults. Addressed only by US Nameplate (account 1621).'
    );
    if (!partnerId) return { success: false, error: 'Failed to create Cat Antares partner' };

    const asnTemplateId = await createTemplateAndAssign(
      partnerId,
      'Caterpillar Antares',
      caterpillarAntaresAsnTemplate as Record<string, unknown>,
      '856',
    );
    const invoiceTemplateId = await createTemplateAndAssign(
      partnerId,
      'Caterpillar Antares',
      caterpillarAntaresInvoiceTemplate as Record<string, unknown>,
      '810',
    );

    return {
      success: true,
      partner: partnerId,
      asnTemplate: asnTemplateId,
      invoiceTemplate: invoiceTemplateId,
    };
  } catch (error) {
    console.error('Error seeding Caterpillar Antares:', error);
    return { success: false, error };
  }
}

export async function seedSampleTradingPartners() {
  try {
    await seedCaterpillarTradingPartner();

    const fordId = await getOrCreatePartner(
      'FORD', 'Ford Motor Company', 'Automotive manufacturer with specific EDI requirements'
    );
    if (fordId) {
      await createTemplateAndAssign(fordId, 'Ford', genericTemplate as Record<string, unknown>, '856');
    }

    const gmId = await getOrCreatePartner(
      'GM', 'General Motors', 'Automotive manufacturer'
    );
    if (gmId) {
      await createTemplateAndAssign(gmId, 'GM', genericTemplate as Record<string, unknown>, '856');
    }

    console.log('Sample trading partners seeded');
    return { success: true };
  } catch (error) {
    console.error('Error seeding sample trading partners:', error);
    return { success: false, error };
  }
}

// Expose for browser console usage
if (typeof window !== 'undefined') {
  (window as Record<string, unknown>).seedCaterpillar = seedCaterpillarTradingPartner;
  (window as Record<string, unknown>).seedCaterpillarAntares = seedCaterpillarAntaresTradingPartner;
  (window as Record<string, unknown>).seedSamplePartners = seedSampleTradingPartners;
  (window as Record<string, unknown>).supabase = supabase;
}
